/**
 * readEngineLine — canonical wire→engine projection for READ surfaces.
 *
 * PT-53A W1.7 · DEF-099.
 *
 *   "Route through `marginEngine.computeLineItem` so no code path anywhere
 *    multiplies qty × rate in JSX. Money is derived once, in the engine,
 *    from the line's own inputs — never in a render function, and never
 *    trusted to arrive pre-computed." — W1.7 direction
 *
 * ─── Why this exists ─────────────────────────────────────────────────────
 *
 * Every read surface in the app (QuoteDetail Flat, QuoteDetail Areas,
 * JobDetail Quote Items panel, ChangeOrderDetail, ChangeOrderReviewSend,
 * QuoteReviewSend) receives a wire line-item in a different shape:
 *
 *   • Flat mode  · quote.line_items[]          camelCase + snake_case mix
 *   • Areas mode · quote.areas[].subAreas[].lineItems[]  price-only projection
 *   • JobDetail  · job.line_items[]            different camelCase set
 *   • CO detail  · co.lineItems[] / co.line_items[]
 *
 * Every consumer has its own defensive destructure with `||` fallbacks and
 * every one of them is a candidate for the DEF-097 / DEF-099 class of bug:
 *   • silently multiplying qty × price
 *   • fabricating $0.00 when a field is absent
 *   • trusting a stored total the backend has since recomputed
 *
 * `readEngineLine` normalises the wire into `computeLineItem`'s input shape
 * and returns the engine's coherent output alongside a `resolution`
 * annotation that tells the caller which fields are trustworthy.
 *
 * ─── Contract ────────────────────────────────────────────────────────────
 *
 *   readEngineLine(wireLine, marginContext) →
 *     {
 *       // pass-through: exactly what computeLineItem returned
 *       total: number,                   // cents
 *       cost: number,                    // cents
 *       margin: number,                  // 0..1
 *       marginSource: string,            // 'price_override' | 'line_override' | …
 *
 *       // resolution signal — the caller uses these to decide render tone:
 *       hasWirePrice: boolean,           // wire supplied a line_total > 0
 *       hasWireCost: boolean,            // wire supplied a resolvable cost basis
 *       hasWireMargin: boolean,          // wire supplied a resolvable margin
 *       wireLineTotal: number | null,    // for disagreement checks (W1.7b)
 *
 *       // trust flag — false when the line is "priced but unexplained":
 *       // has a total but no cost/margin backing it. The engine returned
 *       // margin=100% by back-calculation (marginFromPrice(0, price) = 1),
 *       // which is arithmetically correct but customer-misleading. Callers
 *       // should render `—` for cost + margin in this state.
 *       hasExplainedMargin: boolean,
 *     }
 *
 * ─── Ground rule ─────────────────────────────────────────────────────────
 *
 * If this helper returns `{total: 0, hasWirePrice: false, hasWireCost: false}`
 * the caller MUST render '—' for the total, NOT `$0.00`. `$0.00` implies a
 * priced zero-value line ("this is free"); '—' implies "not enough
 * information to display a total." They are not the same thing.
 */

import { computeLineItem } from './marginEngine';
import { normalizeMarginValue as normalizeMargin } from './quotePrefill';

/** Coerce any wire-side numeric to a finite Number, else null. */
function toFiniteNumber(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : null;
}

// PR-12: consolidated — use the hardened normalizer from quotePrefill
// that rejects >= 1.0 (margin >= 100% = negative price) instead of passing it through.

/**
 * Resolve the shared marginContext for a quote or job payload.
 * Centralised so QuoteDetail (Flat + Areas), JobDetail, and future callers
 * all build the marginContext the SAME way — otherwise a "same line" can
 * evaluate differently on two surfaces and the W1.7c cross-surface
 * invariant fails.
 *
 * @param {object} quoteOrJob  A quote/job payload from the API.
 * @param {object} [customer]  Optional customer record for customer-margin.
 */
export function resolveMarginContext(quoteOrJob, customer = null) {
  const q = quoteOrJob || {};
  const c = customer || {};
  return {
    quoteMargin: normalizeMargin(q.overall_margin ?? q.overallMargin),
    customerMargin: normalizeMargin(c.default_margin ?? c.defaultMargin),
    customerTypeMargin: normalizeMargin(c.customer_type_default_margin ?? c.customerTypeDefaultMargin),
  };
}

/**
 * Wire → engine-input projection. Accepts every observed shape.
 *
 * Field resolution (each `??` chain is deliberate; do NOT re-order):
 *   materialCost ← materialCost | material_cost
 *   laborCost    ← laborCost    | labor_cost
 *   otherCost    ← otherCost    | other_cost
 *   (if the discrete costs are absent, `cost_basis` acts as a total-cost
 *    fallback via `materialCost = cost_basis`, `laborCost/otherCost = 0`)
 *   lineTotal    ← lineTotal | line_total | totalCostCents
 *   lineMargin   ← lineMargin | margin_pct | marginPct
 *   quantity     ← quantity | sqft | sq_ft
 *   unit         ← unit | unitOfMeasure | unit_of_measure
 */
function projectToEngineInput(wire) {
  const w = wire || {};

  const materialCost = toFiniteNumber(w.materialCost ?? w.material_cost);
  const laborCost = toFiniteNumber(w.laborCost ?? w.labor_cost);
  const otherCost = toFiniteNumber(w.otherCost ?? w.other_cost);
  const costBasis = toFiniteNumber(w.costBasis ?? w.cost_basis);

  // If discrete costs are missing but `cost_basis` is present, stuff it
  // into materialCost so the engine has a truthful cost input. This
  // preserves the invariant `cost === cost_basis` for wire records that
  // only carried the aggregate.
  const hasDiscreteCosts = materialCost !== null || laborCost !== null || otherCost !== null;
  const hasAnyCost = hasDiscreteCosts || costBasis !== null;

  const engineInput = {
    materialCost: hasDiscreteCosts ? (materialCost ?? 0) : (costBasis ?? 0),
    laborCost: hasDiscreteCosts ? (laborCost ?? 0) : 0,
    otherCost: hasDiscreteCosts ? (otherCost ?? 0) : 0,
    // Normalized margin (percent-form values coerced to decimal).
    lineMargin: normalizeMargin(w.lineMargin ?? w.margin_pct ?? w.marginPct),
    categoryMargin: normalizeMargin(w.categoryMargin ?? w.category_margin),
    isCustom: Boolean(w.isCustom ?? w.is_custom),
  };

  // lineTotal takes the price-override branch in the engine — feed it only
  // when the wire actually supplies a positive stored total. If we always
  // set lineTotal=0 we'd trigger the cost+margin branch, which for a
  // price-only wire (Areas mode) yields total=0 falsely.
  const wireLineTotal =
    toFiniteNumber(w.lineTotal) ??
    toFiniteNumber(w.line_total) ??
    toFiniteNumber(w.totalCostCents);
  const hasWirePrice = wireLineTotal !== null && wireLineTotal > 0;
  if (hasWirePrice) {
    engineInput.lineTotal = wireLineTotal;
  }

  const hasWireMargin = engineInput.lineMargin !== null && Number.isFinite(engineInput.lineMargin);

  return {
    engineInput,
    hasWirePrice,
    hasWireCost: hasAnyCost,
    hasWireMargin,
    wireLineTotal,
  };
}

/**
 * Read-surface entry point. Feeds the wire line-item through the engine
 * and annotates the result with resolution signals.
 *
 * @param {object} wireLine        Any-shape wire line-item.
 * @param {object|number} [ctx]    marginContext (resolveMarginContext output
 *                                 or a bare quoteMargin number).
 */
export function readEngineLine(wireLine, ctx = 0) {
  const { engineInput, hasWirePrice, hasWireCost, hasWireMargin, wireLineTotal } =
    projectToEngineInput(wireLine);

  const computed = computeLineItem(engineInput, ctx);

  // "Explained margin" — the margin figure is trustworthy for display iff
  // it originated from wire data, not from the engine back-calculating
  // 100% because cost was zero.
  //   • price_override path: margin was derived from cost/price. Trustworthy
  //     ONLY if the wire supplied a real cost. Otherwise it's 100% by dead
  //     reckoning — render '—'.
  //   • non-price_override path: margin came from the cascade (line, category,
  //     customer, customer-type, quote). Trustworthy iff any of those inputs
  //     was actually set on wire or context.
  const hasExplainedMargin =
    computed.marginSource === 'price_override'
      ? hasWireCost
      : hasWireMargin || computed.marginSource !== 'quote_default' || ctxHasAnyMargin(ctx);

  return {
    total: computed.total,
    cost: computed.cost,
    margin: computed.margin,
    marginSource: computed.marginSource,
    hasWirePrice,
    hasWireCost,
    hasWireMargin,
    wireLineTotal,
    hasExplainedMargin,
  };
}

/**
 * previewLineTotal — edit-surface helper for the live preview total.
 *
 * The edit-surface line-item is being typed by the user; there is no stored
 * `line_total` yet, and typically no `cost_basis`/`lineMargin` either —
 * just a working `quantity` and a working `unit_price` (cents). The edit
 * surface's live-preview total is `quantity × unit_price` — which is
 * exactly the arithmetic the READ surface must NEVER do.
 *
 * The edit surface can legitimately do it because:
 *   (a) the user is authoring the rate in the same frame,
 *   (b) the total is display-only — the write-back is `unit_price` +
 *       `quantity`, not the derived total (verified: SubAreaEditor:97 and
 *       :107 are the sole consumers, neither reaches onChange),
 *   (c) rounding to whole cents matches the engine's own rounding rule.
 *
 * This helper exists so BOTH edit-surface sites use the SAME formula and
 * the SAME rounding as the engine, keeping edit and detail surfaces in
 * lock-step (an editor showing $9.99 while detail shows $10.00 is a defect
 * a user will report).
 *
 * @param {number} quantity
 * @param {number} unitPriceCents
 * @returns {number} rounded cents
 */
export function previewLineTotal(quantity, unitPriceCents) {
  const q = toFiniteNumber(quantity) ?? 0;
  const p = toFiniteNumber(unitPriceCents) ?? 0;
  if (q === 0 || p === 0) return 0;
  return Math.round(q * p);
}

function ctxHasAnyMargin(ctx) {
  if (typeof ctx === 'number' && Number.isFinite(ctx) && ctx > 0) return true;
  if (ctx && typeof ctx === 'object') {
    return Boolean(
      toFiniteNumber(ctx.quoteMargin) ||
        toFiniteNumber(ctx.customerMargin) ||
        toFiniteNumber(ctx.customerTypeMargin),
    );
  }
  return false;
}

/**
 * W1.7b — disagreement check. Recomputes the total from cost+margin (ignoring
 * the wire's stored `line_total`) and compares to the wire's stored total.
 * Returns null when a comparison isn't possible (missing inputs), otherwise
 * an object describing the delta. Callers use this to fire an inconsistency
 * notice per DEF-081 treatment.
 *
 * ε: 1 cent absolute — anything under a cent is engine rounding, not drift.
 */
export function detectWireDisagreement(wireLine, ctx = 0) {
  const { engineInput, hasWirePrice, hasWireCost, hasWireMargin, wireLineTotal } =
    projectToEngineInput(wireLine);

  // §5 (P0, 2026-08-08): custom lines are USER-PRICED — the user typed the
  // total directly, not a cost/margin ratio. There is no "engine computes vs
  // stored" comparison for them: the user IS the engine. Recomputing against
  // any cost/margin the user happened to enter produces a false-positive
  // consistency warning on every custom line. Skip.
  if (engineInput.isCustom) return null;

  // Both a wire price AND enough info to independently recompute are
  // required for a meaningful check. If we can't recompute independently,
  // there's no "computed vs stored" — there's just stored.
  const canRecompute = hasWireCost && (hasWireMargin || ctxHasAnyMargin(ctx));
  if (!hasWirePrice || !canRecompute) return null;

  // Strip lineTotal so the engine takes the cost+margin branch.
  const { lineTotal, ...costMarginInput } = engineInput; // eslint-disable-line no-unused-vars
  const computed = computeLineItem(costMarginInput, ctx);

  const delta = Math.abs(computed.total - wireLineTotal);
  if (delta <= 1) return null;

  return {
    wireLineTotal,
    computedTotal: computed.total,
    deltaCents: delta,
  };
}
